import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as Icons from 'react-icons/fa';
import emailjs from '@emailjs/browser';

const { FaPaperPlane, FaEnvelope, FaUser, FaComment } = Icons;

// Helper to render icons safely
const Icon = ({ Component, ...props }: { Component: any; [key: string]: any }) => {
  return <Component {...props} />;
};

interface ContactFormProps {
  language: 'tr' | 'en';
}

// Ana form component'i (reCAPTCHA opsiyonel)
const ContactFormBase: React.FC<ContactFormProps & { executeRecaptcha?: (action: string) => Promise<string> }> = ({ language, executeRecaptcha }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // reCAPTCHA doğrulaması (opsiyonel)
      let recaptchaToken = '';
      if (executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha('contact_form');
        } catch (recaptchaError) {
          console.warn('reCAPTCHA error:', recaptchaError);
          // reCAPTCHA hatası olsa bile form gönderimine devam et
        }
      }
      
      // EmailJS ile mail gönder
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_9r4dtie';
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_0wu600o';
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'o8ZdPn2cBb-B0zuOz';

      if (!serviceId || !templateId || !publicKey) {
        // EmailJS yapılandırılmamışsa fallback olarak mailto kullan
        const mailtoLink = `mailto:furkan@furkangul.net?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(
          language === 'tr' 
            ? `İsim: ${formData.name}\nEmail: ${formData.email}\n\nMesaj:\n${formData.message}`
            : `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
        )}`;
        window.location.href = mailtoLink;
        
        setTimeout(() => {
          setSubmitStatus('success');
          setIsSubmitting(false);
          setFormData({ name: '', email: '', subject: '', message: '' });
          setTimeout(() => setSubmitStatus('idle'), 3000);
        }, 1000);
        return;
      }

      // EmailJS template parametreleri
      const templateParams: any = {
        from_name: formData.name,
        from_email: formData.email,
        subject: formData.subject,
        message: formData.message,
        to_email: 'furkan@furkangul.net',
        reply_to: formData.email
      };
      
      // reCAPTCHA token varsa ekle
      if (recaptchaToken) {
        templateParams.recaptcha_token = recaptchaToken;
      }

      // EmailJS ile mail gönder
      await emailjs.send(serviceId, templateId, templateParams, publicKey);

      setSubmitStatus('success');
      setIsSubmitting(false);
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setSubmitStatus('idle'), 5000);
    } catch (error) {
      console.error('EmailJS error:', error);
      setSubmitStatus('error');
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  return (
    <motion.form
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      onSubmit={handleSubmit}
      className="space-y-4"
    >
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {language === 'tr' ? 'İsim' : 'Name'}
        </label>
        <div className="relative">
          <Icon Component={FaUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full pl-10 pr-4 py-3 bg-white/5 border border-indigo-500/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder={language === 'tr' ? 'Adınız Soyadınız' : 'Your Name'}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {language === 'tr' ? 'Email' : 'Email'}
        </label>
        <div className="relative">
          <Icon Component={FaEnvelope} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full pl-10 pr-4 py-3 bg-white/5 border border-indigo-500/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder={language === 'tr' ? 'email@example.com' : 'email@example.com'}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {language === 'tr' ? 'Konu' : 'Subject'}
        </label>
        <input
          type="text"
          name="subject"
          value={formData.subject}
          onChange={handleChange}
          required
            className="w-full px-4 py-3 bg-white/5 border border-indigo-500/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          placeholder={language === 'tr' ? 'Mesaj konusu' : 'Message subject'}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {language === 'tr' ? 'Mesaj' : 'Message'}
        </label>
        <div className="relative">
          <Icon Component={FaComment} className="absolute left-3 top-3 text-gray-400" />
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows={5}
            className="w-full pl-10 pr-4 py-3 bg-white/5 border border-indigo-500/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            placeholder={language === 'tr' ? 'Mesajınızı buraya yazın...' : 'Write your message here...'}
          />
        </div>
      </div>

      <motion.button
        type="submit"
        disabled={isSubmitting}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="w-full bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 text-white py-3 rounded-lg font-medium hover:shadow-lg hover:shadow-indigo-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {isSubmitting ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            <span>{language === 'tr' ? 'Gönderiliyor...' : 'Sending...'}</span>
          </>
        ) : (
          <>
            <Icon Component={FaPaperPlane} />
            <span>{language === 'tr' ? 'Mesaj Gönder' : 'Send Message'}</span>
          </>
        )}
      </motion.button>

      {submitStatus === 'success' && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-3 bg-green-500/20 border border-green-500/30 rounded-lg text-green-300 text-sm text-center"
        >
          {language === 'tr' ? 'Mesajınız başarıyla gönderildi!' : 'Message sent successfully!'}
        </motion.div>
      )}

      {submitStatus === 'error' && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 text-sm text-center"
        >
          {language === 'tr' ? 'Bir hata oluştu. Lütfen tekrar deneyin.' : 'An error occurred. Please try again.'}
        </motion.div>
      )}
    </motion.form>
  );
};

// reCAPTCHA ile sarmalı component (sadece key varsa kullanılır)
const ContactFormWithRecaptcha: React.FC<ContactFormProps> = ({ language }) => {
  // Dynamic import ile reCAPTCHA'yı sadece gerektiğinde yükle
  const [RecaptchaProvider, setRecaptchaProvider] = React.useState<any>(null);
  const [useRecaptchaHook, setUseRecaptchaHook] = React.useState<any>(null);
  const [isLoaded, setIsLoaded] = React.useState(false);

  React.useEffect(() => {
    import('react-google-recaptcha-v3').then((module) => {
      setRecaptchaProvider(() => module.GoogleReCaptchaProvider);
      setUseRecaptchaHook(() => module.useGoogleReCaptcha);
      setIsLoaded(true);
    });
  }, []);

  if (!isLoaded || !RecaptchaProvider || !useRecaptchaHook) {
    // Yüklenene kadar normal formu göster
    return <ContactFormBase language={language} />;
  }

  const ContactFormInner: React.FC<ContactFormProps> = ({ language }) => {
    const { executeRecaptcha } = useRecaptchaHook();
    return <ContactFormBase language={language} executeRecaptcha={executeRecaptcha} />;
  };

  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

  return (
    <RecaptchaProvider
      reCaptchaKey={recaptchaSiteKey}
      scriptProps={{
        async: false,
        defer: false,
        appendTo: "head",
        nonce: undefined,
      }}
    >
      <ContactFormInner language={language} />
    </RecaptchaProvider>
  );
};

// Ana component - reCAPTCHA key'e göre karar verir
const ContactForm: React.FC<ContactFormProps> = ({ language }) => {
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

  if (!recaptchaSiteKey) {
    // reCAPTCHA key yoksa normal formu göster
    return <ContactFormBase language={language} />;
  }

  // reCAPTCHA key varsa reCAPTCHA ile sarmalı versiyonu göster
  return <ContactFormWithRecaptcha language={language} />;
};

export default ContactForm;
